import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    CardMedia,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    IconButton,
    Alert,
    CircularProgress,
    Chip,
    Tooltip,
    AppBar,
    Toolbar,
    Menu,
    MenuItem as MenuItemComponent,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    ArrowBack as ArrowBackIcon,
    Home as HomeIcon,
    Dashboard as DashboardIcon,
    ViewCarousel as ViewCarouselIcon,
    Menu as MenuIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import dayjs from "dayjs";
import {
    getAds,
    createAd,
    updateAd,
    deleteAd,
    updateAdSortOrder,
} from "../../../api/api";
import { getCurrentBranchId } from "../../../utils/auth";
import AdForm from "./AdForm";
import BulkUpload from "./BulkUpload";
import Logo from "../../../assests/logo.png";

// Sortable Item Component
const SortableAdItem = ({ ad, onEdit, onDelete, onToggleActive }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: ad.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const getStatusColor = (ad) => {
        const today = dayjs();
        const startDate = dayjs(ad.start_date);
        const endDate = dayjs(ad.end_date);

        if (!ad.is_active) return "default";
        if (today.isBefore(startDate)) return "warning";
        if (today.isAfter(endDate)) return "error";
        return "success";
    };

    const getStatusText = (ad) => {
        const today = dayjs();
        const startDate = dayjs(ad.start_date);
        const endDate = dayjs(ad.end_date);

        if (!ad.is_active) return "Inactive";
        if (today.isBefore(startDate)) return "Scheduled";
        if (today.isAfter(endDate)) return "Expired";
        return "Active";
    };

    return (
        <Grid item xs={12} sm={6} md={4}>
            <Card ref={setNodeRef} style={style}>
                <Box sx={{ position: "relative" }}>
                    <CardMedia
                        component="img"
                        height="200"
                        image={ad.image_url}
                        alt={ad.title}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            display: "flex",
                            gap: 1,
                        }}
                    >
                        <Chip
                            label={getStatusText(ad)}
                            color={getStatusColor(ad)}
                            size="small"
                        />
                        <Chip
                            label={ad.image_type}
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                    <Box
                        {...attributes}
                        {...listeners}
                        sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            cursor: "grab",
                            color: "white",
                            backgroundColor: "rgba(0,0,0,0.5)",
                            borderRadius: 1,
                            p: 0.5,
                        }}
                    >
                        <DragIcon />
                    </Box>
                </Box>
                <CardContent>
                    <Typography variant="h6" component="h2" noWrap>
                        {ad.title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        {ad.description}
                    </Typography>
                    <Typography variant="caption" display="block">
                        {dayjs(ad.start_date).format("MMM DD")} -{" "}
                        {dayjs(ad.end_date).format("MMM DD, YYYY")}
                    </Typography>
                    <Typography variant="caption" display="block">
                        Order: {ad.sort_order}
                    </Typography>
                </CardContent>
                <CardActions>
                    <Tooltip title={ad.is_active ? "Deactivate" : "Activate"}>
                        <IconButton
                            onClick={() => onToggleActive(ad)}
                            color={ad.is_active ? "success" : "default"}
                        >
                            {ad.is_active ? (
                                <VisibilityIcon />
                            ) : (
                                <VisibilityOffIcon />
                            )}
                        </IconButton>
                    </Tooltip>
                    <IconButton onClick={() => onEdit(ad)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => onDelete(ad.id)} color="error">
                        <DeleteIcon />
                    </IconButton>
                </CardActions>
            </Card>
        </Grid>
    );
};

const AdManagement = () => {
    const navigate = useNavigate();
    const branchId = getCurrentBranchId();
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingAd, setEditingAd] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openBulkUpload, setOpenBulkUpload] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchAds();
    }, []);

    // Fix scrolling issue by overriding global overflow hidden
    useEffect(() => {
        // Override global overflow hidden for this page
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        const bodyElement = document.body;
        
        // Store original styles
        const originalBodyOverflow = bodyElement.style.overflow;
        const originalHtmlOverflow = htmlElement.style.overflow;
        const originalRootOverflow = rootElement ? rootElement.style.overflow : '';
        const originalRootHeight = rootElement ? rootElement.style.height : '';
        
        // Enable scrolling
        bodyElement.style.overflow = 'auto';
        bodyElement.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }
        
        // Add a CSS class to ensure scrolling works
        bodyElement.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');
        
        return () => {
            // Restore original overflow settings when component unmounts
            bodyElement.style.overflow = originalBodyOverflow || 'hidden';
            bodyElement.style.height = '';
            htmlElement.style.overflow = originalHtmlOverflow || 'hidden';
            htmlElement.style.height = '';
            
            if (rootElement) {
                rootElement.style.overflow = originalRootOverflow || 'hidden';
                rootElement.style.height = originalRootHeight || '100%';
            }
            
            bodyElement.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    const fetchAds = async () => {
        try {
            setLoading(true);
            const response = await getAds();
            if (response.success) {
                setAds(response.data);
            } else {
                setError("Failed to fetch ads");
            }
        } catch (err) {
            setError("Error loading ads");
            console.error("Error fetching ads:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAd = () => {
        setEditingAd(null);
        setOpenDialog(true);
    };

    const handleEditAd = (ad) => {
        setEditingAd(ad);
        setOpenDialog(true);
    };

    const handleDeleteAd = async (id) => {
        if (window.confirm("Are you sure you want to delete this ad?")) {
            try {
                const response = await deleteAd(id);
                if (response.success) {
                    setAds(ads.filter((ad) => ad.id !== id));
                } else {
                    setError("Failed to delete ad");
                }
            } catch (err) {
                setError("Error deleting ad");
                console.error("Error deleting ad:", err);
            }
        }
    };

    const handleToggleActive = async (ad) => {
        try {
            const updatedAd = { ...ad, is_active: !ad.is_active };
            const response = await updateAd(ad.id, updatedAd);
            if (response.success) {
                setAds(ads.map((a) => (a.id === ad.id ? response.data : a)));
            } else {
                setError("Failed to update ad status");
            }
        } catch (err) {
            setError("Error updating ad status");
            console.error("Error updating ad:", err);
        }
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setEditingAd(null);
    };

    const handleAdSaved = (savedAd) => {
        if (editingAd) {
            setAds(ads.map((ad) => (ad.id === savedAd.id ? savedAd : ad)));
        } else {
            setAds([...ads, savedAd]);
        }
        handleDialogClose();
    };

    const handleBulkUploadSuccess = (newAds) => {
        setAds([...ads, ...newAds]);
        setOpenBulkUpload(false);
    };

    const handleOpenBulkUpload = () => {
        setOpenBulkUpload(true);
    };

    const handleCloseBulkUpload = () => {
        setOpenBulkUpload(false);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = ads.findIndex((ad) => ad.id === active.id);
            const newIndex = ads.findIndex((ad) => ad.id === over.id);

            const newItems = arrayMove(ads, oldIndex, newIndex);
            setAds(newItems);

            // Update sort order in backend
            try {
                const adsWithNewOrder = newItems.map((ad, index) => ({
                    id: ad.id,
                    sort_order: index,
                }));

                await updateAdSortOrder(adsWithNewOrder);
            } catch (err) {
                console.error("Error updating sort order:", err);
                setError("Failed to update sort order");
                // Revert the change
                setAds(ads);
            }
        }
    };

    // Navbar menu handlers
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleNavigateHome = () => {
        navigate("/home");
        handleMenuClose();
    };

    const handleNavigateDisplayOptions = () => {
        window.open(
            `/home/display_ad_carousel?branch_id=${branchId}`,
            "_blank"
        );
        handleMenuClose();
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto'
        }}>
            {/* Navbar */}
            <AppBar position="static">
                <Toolbar>
                    <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                        <img
                            src={Logo}
                            alt="Logo"
                            style={{
                                height: "32px",
                                marginRight: "16px",
                                filter: "brightness(0) invert(1)", // Make logo white
                            }}
                        />
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="back"
                            onClick={handleNavigateHome}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Box>

                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ flexGrow: 1 }}
                    >
                        Ad Management
                    </Typography>

                    <Button
                        color="inherit"
                        startIcon={<HomeIcon />}
                        onClick={handleNavigateHome}
                        sx={{ mr: 1 }}
                    >
                        Home
                    </Button>

                    <Button
                        color="inherit"
                        startIcon={<ViewCarouselIcon />}
                        onClick={handleNavigateDisplayOptions}
                        sx={{ mr: 1 }}
                    >
                        Display
                    </Button>

                    <IconButton
                        size="large"
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleMenuOpen}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Navigation Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    },
                }}
            >
                <MenuItemComponent onClick={handleNavigateHome}>
                    <HomeIcon sx={{ mr: 1 }} />
                    Admin Home
                </MenuItemComponent>
                <MenuItemComponent
                    onClick={() =>
                        window.open(
                            `/home/display_ad_carousel?branch_id=${branchId}`,
                            "_blank"
                        )
                    }
                >
                    <ViewCarouselIcon sx={{ mr: 1 }} />
                    Main Carousel
                </MenuItemComponent>
                <MenuItemComponent
                    onClick={() =>
                        window.open(
                            `/home/display_ad_Carousel_landscape?branch_id=${branchId}`,
                            "_blank"
                        )
                    }
                >
                    <DashboardIcon sx={{ mr: 1 }} />
                    Landscape Carousel
                </MenuItemComponent>
                <MenuItemComponent
                    onClick={() =>
                        window.open(
                            `/home/display_ad_Carousel_Portrait?branch_id=${branchId}`,
                            "_blank"
                        )
                    }
                >
                    <DashboardIcon sx={{ mr: 1 }} />
                    Portrait Carousel
                </MenuItemComponent>
            </Menu>

            <Box sx={{ 
                p: 3,
                flex: 1,
                overflow: 'auto',
                minHeight: 0
            }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography variant="h4" component="h1">
                        Ad Management
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleOpenBulkUpload}
                        >
                            Bulk Upload
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateAd}
                        >
                            Add New Ad
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={ads.map((ad) => ad.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <Grid container spacing={3}>
                            {ads.map((ad) => (
                                <SortableAdItem
                                    key={ad.id}
                                    ad={ad}
                                    onEdit={handleEditAd}
                                    onDelete={handleDeleteAd}
                                    onToggleActive={handleToggleActive}
                                />
                            ))}
                        </Grid>
                    </SortableContext>
                </DndContext>

                {ads.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            No ads found. Create your first ad to get started!
                        </Typography>
                    </Box>
                )}

                <Dialog
                    open={openDialog}
                    onClose={handleDialogClose}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        {editingAd ? "Edit Ad" : "Create New Ad"}
                    </DialogTitle>
                    <DialogContent>
                        <AdForm
                            ad={editingAd}
                            onSave={handleAdSaved}
                            onCancel={handleDialogClose}
                        />
                    </DialogContent>
                </Dialog>

                {/* Bulk Upload Dialog */}
                {openBulkUpload && (
                    <BulkUpload
                        onClose={handleCloseBulkUpload}
                        onSuccess={handleBulkUploadSuccess}
                    />
                )}
            </Box>
        </Box>
    );
};

export default AdManagement;
